import React, { useEffect, useState } from 'react';
import { Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { AuthProvider, useAuth } from './AuthContext';
import ProtectedRoute from './ProtectedRoute';
import InspectionHistory from './InspectionHistory';
import RegisterPage from './Register';
import HomePage from './Home';
import Incidents from './Incidents';
import IncidentInvestigationDashboard from './IncidentInvestigationDashboard';
import Hazard from './Hazard';
import HazardAssessmentAcknowledgment from './HazardAssessmentAcknowledgment';
import HazardAcknowledgmentPublic from './HazardAcknowledgmentPublic';
import FLHAAcknowledgmentPublic from './FLHAAcknowledgmentPublic';
import FormalHazardAssessment from './FormalHazardAssessment';
import EmployeeManagementDashboard from './EmployeeManagementDashboard';
import FirstAidForm from './forms/FirstAidForm';
import VandalismForm from './forms/VandalismForm';
import WorkRefusalForm from './forms/WorkRefusalForm';
import NearMissForm from './forms/NearMissForm';
import FatalitiesForm from './forms/FatalitiesForm';
import HospitalAdmittanceForm from './forms/HospitalAdmittanceForm';
import InjuriesLostTimeForm from './forms/InjuriesLostTimeForm';
import InjuriesNonLostTimeForm from './forms/InjuriesNonLostTimeForm';
import OHSSeriousForm from './forms/OHSSeriousForm';
import PSIForm from './forms/PSIForm';
import Header from './Header'; // Import the Header component
import ForgotPassword from './ForgotPassword';
import ResetPassword from './ResetPassword';
import Profile from './Profile'; // Import the Profile component
import Unauthorized from './Unauthorized'; // Import the Unauthorized component
import IncidentDetail from './components/IncidentDetail'; // Import the IncidentDetail component
import DelegateDashboard from './components/DelegateDashboard'; // Import the DelegateDashboard component
import DelegateSubmission from './components/DelegateSubmission'; // Import the DelegateSubmission component
import ManagerReviewDashboard from './components/ManagerReview'; // Import the ManagerReview component
import SupervisorDashboard from './components/SupervisorDashboard'; // Import the SupervisorDashboard component
import GeneralDashboard from './components/GeneralDashboard'; // Import the GeneralDashboard component
import IncidentReview from './components/IncidentReview'; // Import the IncidentReview component
import Login from './Login'; // Import the Login component
import WCBClerkIncidentReview from './WCBClerkIncidentReview';
import WCBClerkIncidentList from './WCBClerkIncidentList';
import HazardAssessmentHistory from './HazardAssessmentHistory';
import InspectionApp from './InspectionApp';
import MaintenanceLog from './MaintenanceLog';
import ContractorPrequalification from './ContractorPrequalification';
import ToolboxMeetingMinutes from './ToolboxMeetingMinutes';
import TrainingModule from './TrainingModule';
import * as VehicleInspectionModule from './VehicleInspection';
import CommitteeMeetingWorkflow from './components/CommitteeMeetingWorkflow';
import CommitteeMeetingRSVP from './components/CommitteeMeetingRSVP';
import CommitteeMeetingsDashboard from './components/CommitteeMeetingsDashboard';
import MinutesSentConfirmation from './components/MinutesSentConfirmation';
import FireTrainingFLHA from './FireTrainingFLHA';
import FLHAHistory from './FLHAHistory';
import FireTrainingFLHASubmitted from './FireTrainingFLHASubmitted';
import FireIncidentReporting from './FireIncidentReporting';
import FireIncidentInvestigations from './FireIncidentInvestigations';
import API from './services/api';
import { hasCommitteeMeetingAccess } from './utils/committeeAccess';
import { getNormalizedUserEmail } from './utils/userIdentity';
import {
  clearMaintenanceDelegateToken,
  extractMaintenanceDelegateTokenFromSearch,
  getMaintenanceDelegateToken,
  hasMaintenanceLogAccessEmail,
  persistMaintenanceDelegateToken,
  stripMaintenanceDelegateTokenFromUrl
} from './utils/maintenanceAccess';
import { hasWcbDashboardAccessEmail } from './utils/wcbAccess';

// Wrapper to extract incidentId from the URL
function WCBClerkIncidentReviewWrapper() {
  const { incidentId } = useParams();
  return <WCBClerkIncidentReview incidentId={incidentId} />;
}

const UnauthenticatedRoute = ({ element: Component }) => {
  const { isAuthenticated } = useAuth();
  const location = useLocation();

  if (isAuthenticated) {
    // Redirect to /acknowledgments after login
    const redirectPath = location.state?.from?.pathname || '/acknowledgments';
    return <Navigate to={redirectPath} />;
  }

  return <Component />;
};

// Protected route for WCB Clerk
function WCBClerkProtectedRoute({ children }) {
  const { user } = useAuth();
  const normalizedEmail = getNormalizedUserEmail(user);
  if (!hasWcbDashboardAccessEmail(normalizedEmail)) {
    return <Navigate to="/unauthorized" replace />;
  }
  return children;
}

function MaintenanceLogProtectedRoute({ children }) {
  const { user, isAuthenticated, loading } = useAuth();
  const location = useLocation();
  const normalizedEmail = getNormalizedUserEmail(user);
  const [isCheckingDelegateAccess, setIsCheckingDelegateAccess] = useState(true);
  const [hasDelegateAccess, setHasDelegateAccess] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const validateDelegateAccess = async () => {
      if (loading) {
        return;
      }

      if (!isAuthenticated) {
        if (!cancelled) {
          setHasDelegateAccess(false);
          setIsCheckingDelegateAccess(false);
        }
        return;
      }

      if (hasMaintenanceLogAccessEmail(normalizedEmail)) {
        if (!cancelled) {
          setHasDelegateAccess(true);
          setIsCheckingDelegateAccess(false);
        }
        return;
      }

      const tokenFromUrl = extractMaintenanceDelegateTokenFromSearch(location.search);
      const storedToken = getMaintenanceDelegateToken();
      const candidateToken = tokenFromUrl || storedToken;

      if (!candidateToken) {
        clearMaintenanceDelegateToken();
        if (!cancelled) {
          setHasDelegateAccess(false);
          setIsCheckingDelegateAccess(false);
        }
        return;
      }

      try {
        const response = await API.post('/api/maintenance-logs/delegate-access/validate', {
          delegateToken: candidateToken
        });

        if (!response.data?.success) {
          throw new Error('Delegate access validation failed');
        }

        persistMaintenanceDelegateToken(candidateToken);

        if (tokenFromUrl && typeof window !== 'undefined' && window.history?.replaceState) {
          window.history.replaceState({}, document.title, stripMaintenanceDelegateTokenFromUrl(location.pathname, location.search, location.hash));
        }

        if (!cancelled) {
          setHasDelegateAccess(true);
        }
      } catch (error) {
        clearMaintenanceDelegateToken();
        if (!cancelled) {
          setHasDelegateAccess(false);
        }
      } finally {
        if (!cancelled) {
          setIsCheckingDelegateAccess(false);
        }
      }
    };

    setIsCheckingDelegateAccess(true);
    validateDelegateAccess();

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, loading, location.hash, location.pathname, location.search, normalizedEmail]);

  if (loading || isCheckingDelegateAccess) {
    return <div>Loading...</div>;
  }

  if (!isAuthenticated) {
    sessionStorage.setItem('redirectAfterLogin', `${location.pathname}${location.search}`);
    return <Navigate to="/login" replace />;
  }

  if (!hasDelegateAccess) {
    return <Navigate to="/unauthorized" replace />;
  }

  return children;
}

function CommitteeMeetingProtectedRoute({ children }) {
  const { user, isAuthenticated, loading } = useAuth();
  const location = useLocation();
  const [profile, setProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const loadProfile = async () => {
      if (loading) {
        return;
      }

      if (!isAuthenticated) {
        if (!cancelled) {
          setProfile(null);
          setProfileLoading(false);
        }
        return;
      }

      try {
        const response = await API.get('/api/users/me');
        if (!cancelled) {
          setProfile(response?.data?.user || response?.data || {});
        }
      } catch (error) {
        if (!cancelled) {
          setProfile({});
        }
      } finally {
        if (!cancelled) {
          setProfileLoading(false);
        }
      }
    };

    if (!loading) {
      setProfileLoading(true);
      loadProfile();
    }

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, loading]);

  if (loading || profileLoading) {
    return <div>Loading...</div>;
  }

  if (!isAuthenticated) {
    sessionStorage.setItem('redirectAfterLogin', `${location.pathname}${location.search}`);
    return <Navigate to="/login" replace />;
  }

  if (!hasCommitteeMeetingAccess({ isAuthenticated, user, profile })) {
    return <Navigate to="/unauthorized" replace />;
  }

  return children;
}

function InspectionHistoryRoute() {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const inspectionId = String(searchParams.get('inspectionId') || '').trim();
  const tab = String(searchParams.get('tab') || 'vehicle').trim().toLowerCase();
  const allowPublicVehicleDeepLink = Boolean(inspectionId) && tab === 'vehicle';

  if (allowPublicVehicleDeepLink) {
    return <InspectionHistory />;
  }

  return (
    <ProtectedRoute>
      <InspectionHistory />
    </ProtectedRoute>
  );
}

// Resolve a usable React component from an imported module (robust to CJS/interop/HMR shapes)
const _resolveComponent = (mod, visited = new Set()) => {
  if (!mod || visited.has(mod)) return null;

  if (typeof mod === 'function') {
    return mod;
  }

  if (typeof mod !== 'object') {
    return null;
  }

  visited.add(mod);

  if ('default' in mod) {
    const resolvedDefault = _resolveComponent(mod.default, visited);
    if (resolvedDefault) {
      return resolvedDefault;
    }
  }

  for (const value of Object.values(mod)) {
    const resolvedValue = _resolveComponent(value, visited);
    if (resolvedValue) {
      return resolvedValue;
    }
  }

  return null;
};

const ResolvedVehicleInspection = _resolveComponent(VehicleInspectionModule) || (() => <div style={{color: 'red'}}>VehicleInspection component not found (check console)</div>);
console.log('ResolvedVehicleInspection type:', typeof ResolvedVehicleInspection, 'VehicleInspectionModule keys:', Object.keys(VehicleInspectionModule || {}));

export default function App() {
  return (
    <AuthProvider>
      <Header />
      <div className="container">
        {/* REMOVED the Router here, as it's already in index.js */}
        <Routes>
          {/* Public routes */}
          <Route path="/" element={<UnauthenticatedRoute element={HomePage} />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<UnauthenticatedRoute element={RegisterPage} />} />
          <Route path="/forgot-password" element={<UnauthenticatedRoute element={ForgotPassword} />} />
          <Route path="/reset-password/:token" element={<ResetPassword />} />
          <Route path="/profile" element={<Profile />} />
          <Route path="/unauthorized" element={<Unauthorized />} />
          <Route path="/minutes-sent" element={<ProtectedRoute><MinutesSentConfirmation /></ProtectedRoute>} />

          {/* Role-based redirection for dashboard */}
          <Route path="/dashboard" element={<SupervisorDashboard />} />
          
          {/* Dedicated routes for each role's dashboard */}
          <Route path="/supervisor-dashboard" element={<SupervisorDashboard />} />
          <Route path="/general-dashboard" element={<GeneralDashboard />} />
          <Route path="/incident-reports" element={<ProtectedRoute><Incidents /></ProtectedRoute>} />
          <Route path="/incidents/:incidentId" element={<ProtectedRoute><IncidentDetail /></ProtectedRoute>} />

          {/* Protected routes */}
          <Route path="/hazard-forms" element={<ProtectedRoute path="/hazard-forms" element={<Hazard />} />} />
          <Route
            path="/acknowledgments"
            element={<ProtectedRoute path="/acknowledgments" element={<HazardAssessmentAcknowledgment />} />}
          />
          <Route
            path="/formal-hazard-assessment"
            element={
              <ProtectedRoute
                path="/formal-hazard-assessment"
                element={<FormalHazardAssessment />}
              />
            }
          />
          <Route path="/forms/first-aid" element={<ProtectedRoute path="/forms/first-aid" element={<FirstAidForm />} />} />
          <Route path="/forms/vandalism" element={<ProtectedRoute path="/forms/vandalism" element={<VandalismForm />} />} />
          <Route path="/forms/work-refusal" element={<ProtectedRoute path="/forms/work-refusal" element={<WorkRefusalForm />} />} />
          <Route path="/forms/near-miss" element={<ProtectedRoute path="/forms/near-miss" element={<NearMissForm />} />} />
          <Route path="/forms/fatalities" element={<ProtectedRoute path="/forms/fatalities" element={<FatalitiesForm />} />} />
          <Route path="/forms/hospital-admittance" element={<ProtectedRoute path="/forms/hospital-admittance" element={<HospitalAdmittanceForm />} />} />
          <Route path="/forms/injuries-lost-time" element={<ProtectedRoute path="/forms/injuries-lost-time" element={<InjuriesLostTimeForm />} />} />
          <Route path="/forms/injuries-non-lost-time" element={<ProtectedRoute path="/forms/injuries-non-lost-time" element={<InjuriesNonLostTimeForm />} />} />
          <Route path="/forms/ohs-serious" element={<ProtectedRoute path="/forms/ohs-serious" element={<OHSSeriousForm />} />} />
          <Route path="/forms/psi" element={<ProtectedRoute path="/forms/psi" element={<PSIForm />} />} />

          <Route path="/incident-investigations" element={
            <ProtectedRoute 
              path="/incident-investigations" 
              element={<IncidentInvestigationDashboard />} 
            />
          } />

          <Route path="/vehicle-inspection" element={<ProtectedRoute path="/vehicle-inspection" element={<ResolvedVehicleInspection />} />} />
          <Route path="/maintenance-log" element={<MaintenanceLogProtectedRoute><MaintenanceLog /></MaintenanceLogProtectedRoute>} />
          <Route path="/contractor-prequalification" element={<ProtectedRoute path="/contractor-prequalification" element={<ContractorPrequalification />} />} />
          <Route path="/toolbox-meetings" element={<ProtectedRoute path="/toolbox-meetings" element={<ToolboxMeetingMinutes />} />} />
          <Route path="/training-module" element={<ProtectedRoute path="/training-module" element={<TrainingModule />} />} />
          <Route path="/inspection-app" element={<ProtectedRoute path="/inspection-app" element={<InspectionApp />} />} />
          <Route path="/committee-meeting" element={<CommitteeMeetingProtectedRoute><CommitteeMeetingWorkflow /></CommitteeMeetingProtectedRoute>} />
          <Route path="/committee-meetings" element={<CommitteeMeetingProtectedRoute><CommitteeMeetingsDashboard /></CommitteeMeetingProtectedRoute>} />
          
          {/* Fire Department Routes */}
          <Route path="/fire-training-flha" element={<ProtectedRoute path="/fire-training-flha" element={<FireTrainingFLHA />} />} />
          <Route path="/fire-training-flha-submitted" element={<ProtectedRoute path="/fire-training-flha-submitted" element={<FireTrainingFLHASubmitted />} />} />
          <Route path="/fire-training-flha-history" element={<ProtectedRoute path="/fire-training-flha-history" element={<FLHAHistory />} />} />
          <Route path="/fire-incident-reports" element={<ProtectedRoute path="/fire-incident-reports" element={<FireIncidentReporting />} />} />
          <Route path="/fire-incident-investigations" element={<ProtectedRoute path="/fire-incident-investigations" element={<FireIncidentInvestigations />} />} />
          
          {/* Public RSVP page using token-based access */}
          <Route path="/committee-rsvp" element={<CommitteeMeetingRSVP />} />
          <Route path="/employee-dashboard" element={<ProtectedRoute path="/employee-dashboard" element={<EmployeeManagementDashboard />} />} />
          <Route path="/home" element={<ProtectedRoute element={<HomePage />} path="/home" />} />
          <Route path="/delegate-dashboard" element={
            <ProtectedRoute>
              <DelegateDashboard />
            </ProtectedRoute>
          } />
          <Route path="/incidents/:incidentId/delegate-submit" element={
            <ProtectedRoute>
              <DelegateSubmission />
            </ProtectedRoute>
          } />
          <Route 
            path="/manager-review" 
            element={
              <ProtectedRoute>
                <ManagerReviewDashboard />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/supervisor-dashboard" 
            element={<SupervisorDashboard />} /> {/* TEMPORARY FIX: Allow for testing */}
          <Route 
            path="/incidents/:incidentId/review" 
            element={<IncidentReview />} />
          <Route path="/wcb-clerk-incident-review" element={
            <WCBClerkProtectedRoute>
              <WCBClerkIncidentReview incidentId={1} />
            </WCBClerkProtectedRoute>
          } />
          <Route
            path="/wcb-dashboard"
            element={
              <WCBClerkProtectedRoute>
                <WCBClerkIncidentList />
              </WCBClerkProtectedRoute>
            }
          />
          <Route
            path="/wcb-dashboard/:incidentId"
            element={
              <WCBClerkProtectedRoute>
                <WCBClerkIncidentReviewWrapper />
              </WCBClerkProtectedRoute>
            }
          />
          <Route
            path="/wcb-review"
            element={
              <WCBClerkProtectedRoute>
                <WCBClerkIncidentList />
              </WCBClerkProtectedRoute>
            }
          />
          <Route
            path="/wcb-review/:incidentId"
            element={
              <WCBClerkProtectedRoute>
                <WCBClerkIncidentReviewWrapper />
              </WCBClerkProtectedRoute>
            }
          />
          <Route path="/hazard-assessment-history" element={<HazardAssessmentHistory />} />
          <Route path="/inspection-history" element={<InspectionHistoryRoute />} />
          <Route path="/hazard-acknowledge" element={<HazardAcknowledgmentPublic />} />
          <Route path="/flha-acknowledge" element={<FLHAAcknowledgmentPublic />} />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </div>
    </AuthProvider>
  );
}
